import { useEffect, useMemo, useState } from 'react';
import axios from '../axios';

const CACHE_TTL_MS = 5 * 60 * 1000;

let cachedCategoryTree = null;
let cachedAt = 0;
let pendingPromise = null;
const subscribers = new Set();

const normalizeCategoryTreeResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.tree)) return data.tree;
  return [];
};

const notifySubscribers = (tree) => {
  subscribers.forEach((callback) => {
    try {
      callback(tree);
    } catch (err) {
      console.error('useCategoryTree subscriber error', err);
    }
  });
};

const fetchCategoryTreeFromApi = async () => {
  const response = await axios.get('/api/categories-tree');
  return normalizeCategoryTreeResponse(response.data);
};

export const getTopCategoriesFromTree = (tree = []) =>
  (Array.isArray(tree) ? tree : []).filter(
    (category) =>
      category && (category.parent_id === null || category.parent_id === undefined)
  );

export const getCategoryTreeCached = async (force = false) => {
  const now = Date.now();
  const isCacheValid = cachedCategoryTree && now - cachedAt < CACHE_TTL_MS;

  if (!force && isCacheValid) {
    return cachedCategoryTree;
  }

  if (!force && pendingPromise) {
    return pendingPromise;
  }

  pendingPromise = fetchCategoryTreeFromApi()
    .then((tree) => {
      cachedCategoryTree = tree;
      cachedAt = Date.now();
      notifySubscribers(tree);
      return tree;
    })
    .finally(() => {
      pendingPromise = null;
    });

  return pendingPromise;
};

export const clearCategoryTreeCache = () => {
  cachedCategoryTree = null;
  cachedAt = 0;
};

export function useCategoryTree({ enabled = true } = {}) {
  const [categoryTree, setCategoryTree] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const listener = (tree) => setCategoryTree(tree);
    subscribers.add(listener);
    return () => {
      subscribers.delete(listener);
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      setCategoryTree([]);
      return;
    }

    let isMounted = true;
    setIsLoading(true);

    getCategoryTreeCached()
      .then((tree) => {
        if (!isMounted) return;
        setCategoryTree(tree);
        setError(null);
      })
      .catch((err) => {
        console.error('Failed to fetch category tree:', err);
        if (!isMounted) return;
        setCategoryTree([]);
        setError(err);
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [enabled]);

  const topCategories = useMemo(
    () => getTopCategoriesFromTree(categoryTree),
    [categoryTree]
  );

  const refetch = async (force = false) => {
    const tree = await getCategoryTreeCached(force);
    setCategoryTree(tree);
    return tree;
  };

  return { categoryTree, topCategories, isLoading, error, refetch };
}
